import { useCallback, useMemo, useState } from 'react';
import { SURVEY_LIMITS } from '@/constants/residents/corkBoardConstants';
import type {
  CreateSurveyConfig,
  SurveyQuestionConfig,
  SurveyQuestionType,
} from '@/types/residents/corkBoard';

export interface UseSurveyDraftReturn {
  enabled: boolean;
  setEnabled: (v: boolean) => void;

  // Survey settings
  isAnonymous: boolean;
  setIsAnonymous: (v: boolean) => void;
  allowReplies: boolean;
  setAllowReplies: (v: boolean) => void;
  expiresAt: Date | null;
  setExpiresAt: (v: Date | null) => void;

  // Questions
  questions: SurveyQuestionConfig[];
  addQuestion: (type: SurveyQuestionType) => void;
  updateQuestion: (index: number, patch: Partial<SurveyQuestionConfig>) => void;
  removeQuestion: (index: number) => void;
  reorderQuestion: (from: number, to: number) => void;

  // Validation
  errors: string[];
  isValid: boolean;
  toPayload: () => CreateSurveyConfig;
  reset: () => void;
}

export function useSurveyDraft(): UseSurveyDraftReturn {
  const [enabled, setEnabled] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [allowReplies, setAllowReplies] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestionConfig[]>([]);

  const addQuestion = useCallback((type: SurveyQuestionType) => {
    setQuestions((prev) => {
      if (prev.length >= SURVEY_LIMITS.questionsMax) return prev;
      const q: SurveyQuestionConfig = {
        prompt: '',
        type,
        isRequired: true,
        position: prev.length,
        ...(type === 'poll'
          ? { options: [
              { label: '', position: 0 },
              { label: '', position: 1 },
            ] }
          : {}),
        ...(type === 'rating'
          ? { ratingMin: SURVEY_LIMITS.ratingMin, ratingMax: SURVEY_LIMITS.ratingMax }
          : {}),
        ...(type === 'long_text'
          ? { longTextMaxLength: SURVEY_LIMITS.longTextDefaultMaxLength }
          : {}),
      };
      return [...prev, q];
    });
  }, []);

  const updateQuestion = useCallback(
    (index: number, patch: Partial<SurveyQuestionConfig>) => {
      setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
    },
    [],
  );

  const removeQuestion = useCallback((index: number) => {
    setQuestions((prev) =>
      prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, position: i })),
    );
  }, []);

  const reorderQuestion = useCallback((from: number, to: number) => {
    setQuestions((prev) => {
      if (from === to) return prev;
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((q, i) => ({ ...q, position: i }));
    });
  }, []);

  const errors = useMemo(() => {
    if (!enabled) return [];
    const errs: string[] = [];
    if (questions.length < SURVEY_LIMITS.questionsMin) {
      errs.push('Add at least one question.');
    }
    if (questions.length > SURVEY_LIMITS.questionsMax) {
      errs.push(`Maximum ${SURVEY_LIMITS.questionsMax} questions.`);
    }
    questions.forEach((q, i) => {
      if (!q.prompt.trim()) {
        errs.push(`Question ${i + 1}: prompt is required.`);
      }
      if (q.prompt.length > SURVEY_LIMITS.promptMaxLength) {
        errs.push(`Question ${i + 1}: prompt is too long.`);
      }
      if (q.type === 'poll') {
        const opts = q.options ?? [];
        if (opts.length < SURVEY_LIMITS.optionsMin) {
          errs.push(`Question ${i + 1}: needs at least ${SURVEY_LIMITS.optionsMin} options.`);
        }
        if (opts.length > SURVEY_LIMITS.optionsMax) {
          errs.push(`Question ${i + 1}: too many options.`);
        }
        opts.forEach((o, oi) => {
          if (!o.label.trim()) {
            errs.push(`Question ${i + 1} option ${oi + 1}: label is required.`);
          }
        });
      }
    });
    if (expiresAt && expiresAt.getTime() <= Date.now()) {
      errs.push('Expiration must be in the future.');
    }
    return errs;
  }, [enabled, questions, expiresAt]);

  const toPayload = useCallback(
    (): CreateSurveyConfig => ({
      isAnonymous,
      allowReplies,
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
      questions: questions.map((q) => ({
        prompt: q.prompt,
        type: q.type,
        isRequired: q.isRequired,
        ...(q.type === 'poll'
          ? { options: (q.options ?? []).map((o) => ({ label: o.label })) }
          : {}),
        ...(q.type === 'long_text' && q.longTextMaxLength != null
          ? { longTextMaxLength: q.longTextMaxLength }
          : {}),
      })),
    }),
    [isAnonymous, allowReplies, expiresAt, questions],
  );

  const reset = useCallback(() => {
    setEnabled(false);
    setIsAnonymous(false);
    setAllowReplies(false);
    setExpiresAt(null);
    setQuestions([]);
  }, []);

  return {
    enabled,
    setEnabled,
    isAnonymous,
    setIsAnonymous,
    allowReplies,
    setAllowReplies,
    expiresAt,
    setExpiresAt,
    questions,
    addQuestion,
    updateQuestion,
    removeQuestion,
    reorderQuestion,
    errors,
    isValid: errors.length === 0,
    toPayload,
    reset,
  };
}
